import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Cake, PartyPopper, Sparkles, Star } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ordinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const dayLabel = (day) => `${ordinal(day)}`;

const BirthdayCard = ({ person, isStudent }) => {
  const border = person.is_today ? 'border-pink-400 shadow-lg ring-2 ring-pink-200' : 'border-slate-200';
  const bg = person.is_today
    ? 'bg-gradient-to-br from-pink-50 via-amber-50 to-blue-50'
    : 'bg-gradient-to-br from-slate-50 to-white';
  return (
    <div
      className={`relative p-4 rounded-xl border ${border} ${bg} transition-all hover:shadow-md`}
      data-testid={`birthday-card-${isStudent ? 'student' : 'worker'}-${person.id}`}
    >
      {person.is_today && (
        <div className="absolute -top-3 -right-2 rotate-12">
          <div className="flex items-center gap-1 rounded-full bg-pink-500 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white shadow-lg animate-pulse">
            <PartyPopper className="h-3 w-3" /> Today!
          </div>
        </div>
      )}
      <div className="flex items-start gap-3">
        <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${person.is_today ? 'bg-pink-500 text-white' : 'bg-blue-100 text-blue-600'}`}>
          {isStudent ? <Star className="w-6 h-6" /> : <Cake className="w-6 h-6" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <p className="font-semibold text-slate-900 truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {person.name}
            </p>
            {!isStudent && person.role && (
              <Badge className="bg-white/70 text-slate-600 border-slate-300 border text-[10px] capitalize">{person.role}</Badge>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            {dayLabel(person.day_of_month)} {person.month_name}
            {!isStudent && person.department ? ` • ${person.department}` : ''}
          </p>
          <div className="mt-2 flex items-start gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm italic text-slate-700 leading-snug">"{person.wish}"</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const BirthdaySection = () => {
  const [data, setData] = useState({ workers: [], students: [], parents: [], month: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/birthdays`, { withCredentials: true });
        setData(res.data);
      } catch (e) {
        // silent — birthdays are non-critical
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const todayCount = useMemo(
    () => [...data.workers, ...data.students, ...data.parents].filter((p) => p.is_today).length,
    [data]
  );

  if (loading) return null;

  const total = data.workers.length + data.students.length + data.parents.length;
  if (total === 0) return null;

  return (
    <Card className="bg-white border-slate-200 overflow-hidden" data-testid="birthday-section">
      <CardHeader className="bg-gradient-to-r from-pink-50 via-amber-50 to-blue-50 border-b border-slate-200">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-pink-500 text-white flex items-center justify-center">
              <Cake className="w-5 h-5" />
            </div>
            <div>
                <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {data.month_label ? `${data.month_label} Birthdays` : `${data.month} Birthdays`}
              </CardTitle>
              <CardDescription>
                {total} celebration{total > 1 ? 's' : ''} {data.month_label ? 'this month and next' : 'this month'}
                {todayCount > 0 && (
                  <span className="ml-2 inline-flex items-center gap-1 text-pink-600 font-semibold">
                    <PartyPopper className="w-3 h-3" /> {todayCount} today!
                  </span>
                )}
              </CardDescription>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-6 space-y-6">
        {data.workers.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-500 mb-3">Teachers & Admins ({data.workers.length})</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="birthday-workers-grid">
              {data.workers.map((w) => (
                <BirthdayCard key={w.id} person={w} isStudent={false} />
              ))}
            </div>
          </div>
        )}
        {data.parents.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-500 mb-3">Parents ({data.parents.length})</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="birthday-parents-grid">
              {data.parents.map((p) => (
                <BirthdayCard key={p.id} person={p} isStudent={false} />
              ))}
            </div>
          </div>
        )}
        {data.students.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-500 mb-3">Students ({data.students.length})</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="birthday-students-grid">
              {data.students.map((s) => (
                <BirthdayCard key={s.id} person={s} isStudent={true} />
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BirthdaySection;
